import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { hotelRoomsAPI } from '../../lib/api';
import {
    BedDouble, MessageSquare, Plus, Trash2, ArrowUp, ArrowDown, Phone, Mail, X, AlertTriangle,
    Copy, ExternalLink, CreditCard,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { normalizePhoneE164 } from '../../lib/phoneUtils';

const LANGS = ['en', 'fr', 'es'];

export const RoomFeedbackConfig = ({ config, updateConfig, card, cards = [], onSelectCard }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [qLang, setQLang] = useState('en');
    const [rooms, setRooms] = useState([]);
    const [assignedRoomId, setAssignedRoomId] = useState(card?.room_id || '');

    useEffect(() => {
        setAssignedRoomId(card?.room_id || '');
        if (card?.organization_id) {
            hotelRoomsAPI.list(card.organization_id)
                .then(res => setRooms(res.data))
                .catch(() => setRooms([]));
        }
    }, [card?.card_id, card?.organization_id, card?.room_id]);

    const handleRoomAssign = async (roomId) => {
        if (!card) return;
        try {
            if (roomId === '__none__') {
                if (assignedRoomId) await hotelRoomsAPI.assignCard(assignedRoomId, card.card_id, 'unassign');
                setAssignedRoomId('');
                updateConfig('room_number', '');
                toast.success(t('hotelRooms.cardUnassigned', 'Card unassigned'));
            } else {
                const room = rooms.find(r => r.id === roomId);
                await hotelRoomsAPI.assignCard(roomId, card.card_id, 'assign');
                setAssignedRoomId(roomId);
                updateConfig('room_number', room?.room_number || '');
                toast.success(`${t('hotelRooms.cardAssigned', 'Card assigned to room')} ${room?.room_number || ''}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t('hotelRooms.assignFailed', 'Failed to assign card'));
        }
    };
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneNameInput, setPhoneNameInput] = useState('');
    const [emailInput, setEmailInput] = useState('');

    const questions = config.rf_questions || [];
    const phones = config.rf_notification_phones || [];
    const emails = config.rf_notification_emails || [];
    const threshold = Math.min(config.rf_low_rating_threshold ?? 3, 3);

    const setQuestions = (next) => updateConfig('rf_questions', next);

    const updateQuestion = (idx, patch) => {
        const next = questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
        setQuestions(next);
    };
    const moveQuestion = (idx, dir) => {
        const to = idx + dir;
        if (to < 0 || to >= questions.length) return;
        const next = [...questions];
        [next[idx], next[to]] = [next[to], next[idx]];
        setQuestions(next);
    };
    const removeQuestion = (idx) => setQuestions(questions.filter((_, i) => i !== idx));
    const addQuestion = () => setQuestions([...questions, {
        id: `rf_custom_${Date.now()}`, enabled: true, text_en: '', text_fr: '', text_es: '',
    }]);

    const recipientPhone = (p) => (typeof p === 'object' && p !== null) ? (p.phone || p.phone_number || '') : p;
    const recipientName = (p) => (typeof p === 'object' && p !== null) ? (p.name || '') : '';

    const addPhone = () => {
        const raw = phoneInput.trim();
        if (!raw) return;
        const normalized = normalizePhoneE164(raw);
        if (!normalized) {
            toast.error(t('reviewShield.invalidPhone', 'Enter a valid phone number (10+ digits)'));
            return;
        }
        if (phones.some(p => recipientPhone(p) === normalized)) {
            toast.error(t('reviewShield.duplicatePhone', 'This phone number is already added'));
            return;
        }
        updateConfig('rf_notification_phones', [...phones, { phone: normalized, name: phoneNameInput.trim() }]);
        setPhoneInput('');
        setPhoneNameInput('');
    };
    const removePhone = (phone) => updateConfig('rf_notification_phones', phones.filter(p => recipientPhone(p) !== phone));

    const addEmail = () => {
        const email = emailInput.trim().toLowerCase();
        if (!email || !email.includes('@')) return;
        if (emails.includes(email)) {
            toast.error(t('reviewShield.duplicateEmail', 'This email is already added'));
            return;
        }
        updateConfig('rf_notification_emails', [...emails, email]);
        setEmailInput('');
    };
    const removeEmail = (email) => updateConfig('rf_notification_emails', emails.filter(e => e !== email));

    // The physical NFC card is programmed with this permanent Tap Engine URL.
    // Changing the room number never changes it — no reprogramming needed.
    const cardUrl = card ? `${window.location.origin}/api/tap?card=${card.card_id}` : '';
    const copyCardUrl = () => {
        navigator.clipboard.writeText(cardUrl).then(() => toast.success(t('cards.urlCopied', 'Card URL copied')));
    };

    return (
        <div className="space-y-6" data-testid="room-feedback-config">
            {/* Room assignment — bound to the existing Tap Engine Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <BedDouble className="w-4 h-4 text-amber-500" />
                        {t('hotel.rfRoomAssignment', 'Room Assignment')}
                        <a href="/card-guide#welcome-room" className="text-xs font-normal text-amber-600 underline ml-auto" data-testid="rf-card-guide-link">{t('cardGuide.rfHelp', 'How do I program this card?')}</a>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-muted-foreground">{t('hotel.rfOrganization', 'Organization / Hotel')}</Label>
                            <p className="text-sm font-medium mt-0.5" data-testid="rf-card-organization">
                                {card?.organization_name || '-'}{card?.location_name ? ` — ${card.location_name}` : ''}
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">{t('hotel.rfExperience', 'Experience')}</Label>
                            <div className="mt-0.5">
                                <Badge className="bg-amber-500/15 text-amber-600 border border-amber-500/30" data-testid="rf-experience-badge">
                                    {t('hotel.touchpointRoomFeedback', 'Welcome / Room')}
                                </Badge>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                <CreditCard className="w-3 h-3" />{t('hotel.rfTapCard', 'Tap Card')}
                            </Label>
                            {cards.length > 0 && onSelectCard ? (
                                <Select value={card?.card_id || ''} onValueChange={onSelectCard}>
                                    <SelectTrigger className="mt-1" data-testid="rf-tap-card-select">
                                        <SelectValue placeholder={t('hotel.rfSelectCard', 'Select a Tap Engine Card')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cards.map(c => (
                                            <SelectItem key={c.card_id} value={c.card_id}>
                                                {c.card_id}{c.name ? ` — ${c.name}` : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p className="text-sm font-mono font-semibold mt-0.5">{card?.card_id || '-'}</p>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">{t('hotel.rfCardStatus', 'Card ID / Status')}</Label>
                            <div className="flex items-center gap-2 mt-1.5">
                                <span className="text-sm font-mono font-semibold" data-testid="rf-card-id">{card?.card_id || '-'}</span>
                                <Badge variant="outline" className={`text-xs ${card?.status === 'active'
                                    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                                    : 'bg-slate-500/10 text-slate-500 border-slate-400/30'}`}
                                    data-testid="rf-card-status">
                                    {card?.status === 'active' ? t('cards.active', 'Active') : t('cards.inactive', 'Inactive')}
                                </Badge>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label>{t('hotel.rfRoomNumber', 'Room Number')}</Label>
                        <div className="flex flex-col sm:flex-row gap-2 mt-1">
                            <Select value={assignedRoomId || '__none__'} onValueChange={handleRoomAssign}>
                                <SelectTrigger className="sm:w-64" data-testid="rf-room-select">
                                    <SelectValue placeholder={t('hotelRooms.selectRoom', 'Select a room')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">{t('hotelRooms.noRoom', 'No room assigned')}</SelectItem>
                                    {rooms.map(r => (
                                        <SelectItem key={r.id} value={r.id}>
                                            {t('hotelRooms.room', 'Room')} {r.room_number}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/hotel-rooms')} data-testid="rf-manage-rooms-btn">
                                <BedDouble className="w-3.5 h-3.5 mr-1" />{t('hotelRooms.manageRooms', 'Manage Rooms')}
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('hotel.rfRoomNumberHint', 'The Tap Card IS the room. Every submission from this card is tagged with this room number. Reassigning the room updates metadata only — the physical card never needs reprogramming.')}
                        </p>
                    </div>

                    {card && (
                        <div className="border rounded-lg p-3 bg-muted/40" data-testid="rf-card-url-block">
                            <Label className="text-xs text-muted-foreground">{t('hotel.rfCardUrl', 'Card URL (programmed on the physical NFC card / QR)')}</Label>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1.5">
                                <code className="text-xs font-mono bg-background border rounded px-2 py-1.5 flex-1 break-all" data-testid="rf-card-url">{cardUrl}</code>
                                <div className="flex gap-2 shrink-0">
                                    <Button type="button" variant="outline" size="sm" onClick={copyCardUrl} data-testid="rf-copy-url-btn">
                                        <Copy className="w-3.5 h-3.5 mr-1" />{t('common.copy', 'Copy')}
                                    </Button>
                                    <Button type="button" variant="outline" size="sm" onClick={() => navigate('/cards')} data-testid="rf-manage-card-btn">
                                        <ExternalLink className="w-3.5 h-3.5 mr-1" />{t('hotel.rfManageCard', 'Manage Card')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Questions editor */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-amber-500" />
                        {t('hotel.rfQuestions', 'Feedback Questions')}
                        <Badge variant="outline" className="ml-1 text-xs">{questions.filter(q => q.enabled !== false).length} {t('hotel.rfActive', 'active')}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex gap-1" data-testid="rf-question-lang-tabs">
                        {LANGS.map(code => (
                            <button key={code} type="button" onClick={() => setQLang(code)}
                                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                                    qLang === code ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                }`}
                                data-testid={`rf-question-lang-${code}`}>
                                {code.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    {questions.map((q, idx) => (
                        <div key={q.id} className={`border rounded-lg p-3 space-y-2 ${q.enabled === false ? 'opacity-50' : ''}`} data-testid={`rf-question-row-${idx}`}>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-muted-foreground w-8">Q{idx + 1}</span>
                                <Input
                                    value={q[`text_${qLang}`] || ''}
                                    onChange={e => updateQuestion(idx, { [`text_${qLang}`]: e.target.value })}
                                    placeholder={t('hotel.rfQuestionPlaceholder', 'Question text…') + ` (${qLang.toUpperCase()})`}
                                    className="flex-1"
                                    data-testid={`rf-question-text-${idx}`}
                                />
                            </div>
                            <div className="flex items-center justify-between pl-10">
                                <div className="flex items-center gap-2">
                                    <Switch checked={q.enabled !== false} onCheckedChange={v => updateQuestion(idx, { enabled: v })} data-testid={`rf-question-enabled-${idx}`} />
                                    <span className="text-xs text-muted-foreground">{q.enabled !== false ? t('manager.on', 'On') : t('manager.off', 'Off')}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => moveQuestion(idx, -1)} data-testid={`rf-question-up-${idx}`}>
                                        <ArrowUp className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === questions.length - 1} onClick={() => moveQuestion(idx, 1)} data-testid={`rf-question-down-${idx}`}>
                                        <ArrowDown className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500" onClick={() => removeQuestion(idx)} data-testid={`rf-question-delete-${idx}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}

                    <Button variant="outline" size="sm" onClick={addQuestion} data-testid="rf-add-question-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" />{t('hotel.rfAddQuestion', 'Add Question')}
                    </Button>
                </CardContent>
            </Card>

            {/* Alerts */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        {t('hotel.rfAlerts', 'Low-Rating Alerts')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    <div>
                        <Label>{t('hotel.rfThreshold', 'Low Rating Threshold')}</Label>
                        <Select value={String(threshold)} onValueChange={v => updateConfig('rf_low_rating_threshold', parseInt(v))}>
                            <SelectTrigger className="mt-1 w-56" data-testid="rf-threshold-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[1, 2, 3].map(n => (
                                    <SelectItem key={n} value={String(n)}>{t('hotel.rfThresholdOpt', '{{n}}★ or below', { n })}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('hotel.rfThresholdHint', 'An alert fires when ANY individual question is rated at or below this — never the average. 4-5 star responses never trigger private alerts.')}
                        </p>
                    </div>

                    {/* SMS */}
                    <div className="border rounded-lg p-3 space-y-3" data-testid="rf-sms-section">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium flex items-center gap-2">
                                <Phone className="w-4 h-4 text-amber-500" />
                                {t('hotel.rfSmsAlerts', 'SMS Alerts')}
                            </Label>
                            <Switch checked={config.rf_sms_enabled === true} onCheckedChange={v => updateConfig('rf_sms_enabled', v)} data-testid="rf-sms-toggle" />
                        </div>
                        {config.rf_sms_enabled === true && (
                            <>
                                {phones.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {phones.map(p => {
                                            const phone = recipientPhone(p);
                                            const name = recipientName(p);
                                            return (
                                                <Badge key={phone} variant="secondary" className="gap-1 text-xs" data-testid={`rf-phone-badge-${phone}`}>
                                                    {name ? `${name} — ${phone}` : phone}
                                                    <button onClick={() => removePhone(phone)} className="ml-1 hover:text-red-600">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Input value={phoneNameInput} onChange={e => setPhoneNameInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPhone(); } }}
                                        placeholder={t('cardSettings.smsRecipientNamePlaceholder', 'Name (optional)')}
                                        className="sm:w-36" data-testid="rf-phone-name-input" />
                                    <Input type="tel" value={phoneInput} onChange={e => setPhoneInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPhone(); } }}
                                        placeholder="+1 514 555 1234 or 5145551234"
                                        className="flex-1" data-testid="rf-phone-input" />
                                    <Button type="button" variant="outline" size="sm" onClick={addPhone} data-testid="rf-add-phone-btn">
                                        <Plus className="w-3.5 h-3.5 mr-1" />{t('common.add', 'Add')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Email */}
                    <div className="border rounded-lg p-3 space-y-3" data-testid="rf-email-section">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium flex items-center gap-2">
                                <Mail className="w-4 h-4 text-blue-500" />
                                {t('hotel.rfEmailAlerts', 'Email Alerts')}
                            </Label>
                            <Switch checked={config.rf_email_enabled === true} onCheckedChange={v => updateConfig('rf_email_enabled', v)} data-testid="rf-email-toggle" />
                        </div>
                        {config.rf_email_enabled === true && (
                            <>
                                {emails.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {emails.map(email => (
                                            <Badge key={email} variant="secondary" className="gap-1 text-xs" data-testid={`rf-email-badge-${email}`}>
                                                {email}
                                                <button onClick={() => removeEmail(email)} className="ml-1 hover:text-red-600">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <Input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                                        placeholder="manager@hotel.com"
                                        className="flex-1" data-testid="rf-email-input" />
                                    <Button type="button" variant="outline" size="sm" onClick={addEmail} data-testid="rf-add-email-btn">
                                        <Plus className="w-3.5 h-3.5 mr-1" />{t('common.add', 'Add')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
